import {Component, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthService} from '../../service/auth.service';
import {UrlHelper} from '../../helper/url.helper';
import {NotificationHandler} from '../../helper/notification.handler';
import {Role} from '../../model/auth/Role';
import {TenantService} from '../../service/tenant.service';
import {EventType, TrackingService} from '../../service/tracking.service';
import {environment} from '../../../environments/environment';
import {UserService} from '../../service/user.service';
import {CustomValidators, MismatchErrorStateMatcher} from '../../helper/custom.validators';
import {CognitoUser} from 'amazon-cognito-identity-js';
import type {UserStatusType} from '../../model/auth/User';
import {InviteUserDto, UserExistsBaseDto} from '../../model/auth/User';
import {ButtonComponent} from '../../common/button/button.component';
import {HttpErrorResponse} from '@angular/common/http';
import {Locale} from '../../model/Locale';

@Component({
  selector: 'sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent implements OnInit {
  inProgress = false;

  migrationTried = false;

  identifierField = new FormControl('', [
    Validators.required, Validators.minLength(5), Validators.maxLength(128)
  ]);
  passwordField = new FormControl('', [
    Validators.required
  ]);
  signInForm = new FormGroup({
    identifier: this.identifierField,
    password: this.passwordField,
  });

  user?: CognitoUser;

  checkedUser?: UserExistsBaseDto;
  checkedUsername?: string;
  invitationRequestDetails = '';
  invitationRequested = false;

  changePassMode = false;

  returnUrl?: string;

  newPasswordField = new FormControl('', [CustomValidators.password]);
  confirmPassField = new FormControl('', [Validators.required]);

  changePassConfirmForm!: FormGroup;
  matcher = new MismatchErrorStateMatcher();

  readonly locale: Locale;

  constructor(private authService: AuthService,
              private userService: UserService,
              private tenantService: TenantService,
              private trackingService: TrackingService,
              private router: Router,
              private formBuilder: FormBuilder,
              route: ActivatedRoute,
              @Inject(LOCALE_ID) locale: string) {
    this.locale = locale as Locale;

    this.returnUrl = decodeURI(route.snapshot.queryParams.returnUrl as string || '');
  }

  async ngOnInit(): Promise<void> {
    this.tenantService.loadTenantDetails();

    this.changePassConfirmForm = this.formBuilder.group({
      password: this.newPasswordField,
      confirmPass: this.confirmPassField
    }, {validators: CustomValidators.passwordsMatch});

    const session = await this.authService.getSession();
    if (session && session.isValid()) {
      NotificationHandler.notification($localize`:General|This page is not available for authorized users@@general-this-page-is-not-available-for-authorized-users-message:This page is not available for authorized users message`);
      await this.router.navigate([UrlHelper.DEFAULT_AUTH_PATH], {replaceUrl: true});
    }
  }

  get allowSocialSignIn(): boolean {
    return false;
  }

  get studentSignUpUrl(): string {
    return UrlHelper.SIGN_UP_PATH + Role.Student;
  }

  get teacherSignUpUrl(): string {
    return UrlHelper.SIGN_UP_PATH + Role.Teacher;
  }

  get inviteRequestUrl(): string {
    return UrlHelper.INVITE_REQUEST_PATH;
  }

  get passResetUrl(): string {
    return UrlHelper.PASS_RESET_PATH;
  }

  get supportUrl(): string {
    return UrlHelper.SUPPORT_PATH;
  }

  get organizeInfoUrl(): string {
    return `${environment.landing.host}${environment.landing.urls.organize}`;
  }

  shouldDisableSubmit() {
    return !this.signInForm.valid;
  }

  shouldDisableChangePassSubmit() {
    return !this.changePassConfirmForm.valid;
  }

  async signIn() {
    if (this.signInForm.valid) {
      const identifier = (this.identifierField.value as string || '').trim();
      const pass = (this.passwordField.value as string || '').trim();

      this.migrationTried = false;
      this.inProgress = true;

      if (this.checkedUser && this.checkedUsername == identifier) {
        if (!this.checkedUser.id) {
          NotificationHandler.handleWarning($localize`:Sign In|User unauthorized error message@@sign-in-user-unauthorized-error:Credentials are invalid or user does not exist`);
          this.inProgress = false;

          return;
        }

        await this.attemptSignIn(this.checkedUser.id, pass, this.checkedUser.status);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.userService.userExists(identifier, !identifier.includes('@')).subscribe(async user => {
          this.checkedUsername = identifier;
          this.checkedUser = user;

          if (!user || !user.id) {
            NotificationHandler.handleWarning($localize`:Sign In|User unauthorized error message@@sign-in-user-unauthorized-error:Credentials are invalid or user does not exist`);
            this.inProgress = false;

            return;
          }

          await this.attemptSignIn(user.id, pass, user.status);
        }, error => {
          NotificationHandler.handleWarning($localize`:Sign In|User unauthorized error message@@sign-in-user-unauthorized-error:Credentials are invalid or user does not exist`, error);
          this.inProgress = false;
        });
      }
    }
  }

  private async attemptSignIn(username: string, pass: string, userStatus: UserStatusType) {
    try {
      this.user = await this.authService.signIn(username, pass);
      this.trackingService.sendEvent(EventType.login);

      if ('challengeName' in this.user && this.user['challengeName'] == 'NEW_PASSWORD_REQUIRED') {
        this.changePassMode = true;
        this.inProgress = false;
      } else {
        const extras = this.returnUrl
          ? {
            queryParams: {
              returnUrl: encodeURI(this.returnUrl)
            }
          }
          : {};

        if (userStatus == 'FORCE_CHANGE_PASSWORD' || userStatus == 'RESET_REQUIRED') {
          // user password was reset before, but not user successfully signed in, should report updating status
          this.userService.reportUpdateUser(username).subscribe(() => {
          }, error => {
            NotificationHandler.handleError($localize`:User Profile|User profile update report failed notification@@user-profile-report-failed-notification:Profile update report failed`, error);
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
          }, async () => {
            await this.router.navigate([UrlHelper.ROLE_SELECT_PATH], extras);
          });
        } else {
          await this.router.navigate([UrlHelper.ROLE_SELECT_PATH], extras);
        }
      }
    } catch (err) {
      const code: string = (err as {[key: string]: string | undefined})['code'] as string;

      if (code === 'UserNotConfirmedException') {
        // The error happens if the user didn't finish the confirmation step when signing up
        // In this case you need to resend the code and confirm the user
        // About how to resend the code and confirm the user, please check the signUp part
        console.error('UserNotConfirmed', err);
        const extras = this.returnUrl
          ? {
            queryParams: {
              returnUrl: this.returnUrl
            }
          }
          : {};
        await this.router.navigate([`${UrlHelper.SIGN_UP_CONFIRM_PATH}${this.identifierField.value}/${username}`], extras);

        this.inProgress = false;
      } else if (code === 'PasswordResetRequiredException') {
        // The error happens when the password is reset in the Cognito console
        // In this case you need to call forgotPassword to reset the password
        // Please check the Forgot Password part.
        console.error('PasswordResetRequired', err);
        await this.router.navigate([`${UrlHelper.PASS_RESET_CONFIRM_PATH}${this.identifierField.value}/${username}`]);

        this.inProgress = false;
      } else if (code === 'NotAuthorizedException' || code === 'UserNotFoundException') {
        // The error happens when the incorrect password is provided
        // or when the supplied username/email does not exist in the Cognito user pool

        // in case auth failed - try to migrate user from an old pool once, if not - show error
        if (!this.migrationTried) {
          this.migrationTried = true;
          this.userService.migrateUser(false, username, pass).subscribe(result => {
            if (result.retry_auth) {
              void this.attemptSignIn(username, pass, result.status);
            } else {
              NotificationHandler.handleWarning($localize`:Sign In|User unauthorized error message@@sign-in-user-unauthorized-error:Credentials are invalid or user does not exist`);
              this.inProgress = false;
            }
          }, error => {
            NotificationHandler.handleError($localize`:Sign In|User unauthorized error message@@sign-in-user-unauthorized-error:Credentials are invalid or user does not exist`, error);
            this.inProgress = false;
          });
        } else {
          NotificationHandler.handleWarning($localize`:Sign In|User unauthorized error message@@sign-in-user-unauthorized-error:Credentials are invalid or user does not exist`);
          this.inProgress = false;
        }
      } else {
        NotificationHandler.handleWarning(err);

        this.inProgress = false;
      }
    }
  }

  async changePasswordProceed() {
    this.inProgress = true;

    try {
      await this.authService.completeNewPassword(this.user as CognitoUser, this.newPasswordField.value);

      if (this.user) {
        this.userService.reportUpdateUser(this.user.getUsername()).subscribe(() => {
          NotificationHandler.notification($localize`:User Profile|User profile password changed notification@@user-profile-password-changed-notification:Password was changed successfully`);
        }, error => {
          NotificationHandler.handleError($localize`:User Profile|User profile update report failed notification@@user-profile-report-failed-notification:Profile update report failed`, error);
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
        }, async () => {
          await this.router.navigate([UrlHelper.DEFAULT_AUTH_PATH], {replaceUrl: true});
        });
      }
    } catch (err) {
      NotificationHandler.handleError($localize`:Password Reset Confirm|Password Reset failed error message@@password-reset-reset-error:Password reset failed and was not successful`, err);
      this.inProgress = false;
    }
  }

  handleUsernameChange() {
    const identifier = this.identifierField.value as string;
    if (identifier) {
      if (this.checkedUsername != identifier && identifier.includes('@')) {
        this.userService.userExists(identifier, false).subscribe(user => {
          this.checkedUsername = identifier;
          this.checkedUser = user;
          this.invitationRequested = false;
        });
      }
    } else {
      this.checkedUsername = undefined;
      this.checkedUser = undefined;
      this.invitationRequested = false;
    }
  }

  get invitationCanBeRequested(): boolean {
    return !!this.checkedUser
      && !!this.checkedUser.id
      && !!this.checkedUsername
      && this.checkedUsername.includes('@')
      && this.checkedUser.status == 'FORCE_CHANGE_PASSWORD';
  }

  requestInvitation(requestInvitationBtn: ButtonComponent) {
    requestInvitationBtn.disabled = true;

    this.inProgress = true;

    const competition = this.tenantService.getTenant();

    const data: InviteUserDto = {
      email: this.identifierField.value as string,
      role: Role.Student,
      resend: true,
      resend_tenant_name: competition?.name || '',
      resend_tenant_domain: competition?.domain || '',
      resend_locale: this.locale,
    };
    this.userService.inviteUserResend(data).subscribe(() => {
      this.invitationRequestDetails = $localize`:User Profile|User profile invitation requested notification@@user-profile-invitation-requested-notification:New invitation was requested and should arrive in your inbox soon`;
      this.invitationRequested = true;

      NotificationHandler.notification(this.invitationRequestDetails);
    }, error => {
      if (error instanceof HttpErrorResponse) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (error.error && error.error.code == 'Conflict') {
          NotificationHandler.handleWarning($localize`:Notifications|User don't need invitation message message@@notifications-user-do-not-need-invitation-message:This user profile cannot get an invitation anymore. Most likely, an invitation was already accepted before, and the user does not need one anymore.`);
        } else {
          NotificationHandler.handleError($localize`:Notifications|Request user invitation has failed message@@notifications-request-user-invitation-failed-error:Request user invitation has failed`, error);
        }
      } else {
        NotificationHandler.handleError($localize`:Notifications|Request user invitation has failed message@@notifications-request-user-invitation-failed-error:Request user invitation has failed`, error);
      }
    }, () => this.inProgress = false);
  }
}
